import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChatCircleDots, PaperPlaneTilt, ArrowsClockwise, User, CheckCircle, XCircle, MagnifyingGlass, Paperclip, FileText, Image as ImageIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function ViberChatPage() {
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // {phone, client, messages}
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    let prevUnread = 0;
    const load = async () => {
      try {
        const r = await api.get('/viber-chat/conversations');
        if (!mounted) return;
        const items = r.data.items || [];
        const totalUnread = items.reduce((s, c) => s + (c.unread || 0), 0);
        // Detect new unread messages — show a small notification toast
        if (totalUnread > prevUnread && prevUnread !== 0) {
          const diff = totalUnread - prevUnread;
          const newest = items.find(c => c.unread > 0);
          toast.message(`📩 Nova Viber poruka (${diff})`, {
            description: newest ? `${newest.client_name || newest.phone}: ${(newest.last_text || '').substring(0, 60)}` : '',
            duration: 6000,
          });
          // Optional: try Browser Notification API for off-tab alerts
          try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              new Notification('Nova Viber poruka', {
                body: newest ? `${newest.client_name || newest.phone}: ${(newest.last_text || '').substring(0, 80)}` : '',
                icon: '/favicon.ico',
                tag: 'viber-' + (newest?.phone || ''),
              });
            }
          } catch (_) { /* ignore */ }
        }
        prevUnread = totalUnread;
        // Update browser tab title with unread count
        if (typeof document !== 'undefined') {
          document.title = totalUnread > 0 ? `(${totalUnread}) Viber - CRM Finance` : 'CRM Finance';
        }
        setConversations(items);
      } catch { if (mounted) toast.error('Greška pri učitavanju razgovora'); }
      finally { if (mounted) setLoading(false); }
    };
    // Try to request notification permission once
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
    } catch (_) { /* ignore */ }
    load();
    const t = setInterval(load, 15000); // refresh every 15s for quick notifications
    return () => { mounted = false; clearInterval(t); if (typeof document !== 'undefined') document.title = 'CRM Finance'; };
  }, []);

  const openChat = async (phone) => {
    try {
      const r = await api.get(`/viber-chat/${phone}`);
      setActive(r.data);
      // Locally clear unread on the conversation list
      setConversations(prev => prev.map(c => c.phone === phone ? { ...c, unread: 0 } : c));
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 100);
    } catch { toast.error('Greška pri otvaranju razgovora'); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      const r = await api.post(`/viber-chat/${active.phone}/reply`, { text: reply.trim() });
      if (r.data.success) {
        toast.success('Poruka poslata');
        setReply('');
        openChat(active.phone);
      } else {
        toast.error(r.data.error || 'Greška pri slanju');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greška pri slanju');
    } finally { setSending(false); }
  };

  const fileInputRef = useRef(null);
  const onPickFile = () => { if (fileInputRef.current) fileInputRef.current.click(); };
  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !active) return;
    e.target.value = '';
    if (f.size > 200 * 1024 * 1024) { toast.error('Fajl je veći od 200MB'); return; }
    const fd = new FormData();
    fd.append('file', f);
    fd.append('text', reply.trim());
    setSending(true);
    const id = toast.loading(`Šaljem ${f.name}...`);
    try {
      const r = await api.post(`/viber-chat/${active.phone}/upload-and-send`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.dismiss(id);
      if (r.data.success) {
        toast.success(`Fajl poslat: ${f.name}`);
        setReply('');
        openChat(active.phone);
      } else {
        toast.error(r.data.error || 'Greška pri slanju fajla');
      }
    } catch (err) {
      toast.dismiss(id);
      toast.error(err.response?.data?.detail || 'Greška pri uploadu');
    } finally { setSending(false); }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const now = new Date();
      if (d.toDateString() === now.toDateString()) {
        return d.toLocaleTimeString('sr-Latn-ME', { hour: '2-digit', minute: '2-digit' });
      }
      return d.toLocaleString('sr-Latn-ME', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const filtered = conversations.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.client_name || '').toLowerCase().includes(s)
        || c.phone.includes(s)
        || (c.last_text || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4" data-testid="viber-chat-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ChatCircleDots size={26} weight="duotone" className="text-purple-600" />Viber Razgovori</h1>
          <p className="text-sm text-slate-500">Kompletna istorija — poslate i primljene Viber poruke po klijentu.</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)] min-h-[500px]">
        {/* Lista razgovora */}
        <Card className="col-span-12 md:col-span-4 lg:col-span-3 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-slate-200 bg-slate-50">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pretraga..." className="pl-9 h-9 bg-white border-slate-300 text-sm" data-testid="chat-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? <p className="text-center text-slate-400 p-6 text-sm">Učitavanje...</p>
              : filtered.length === 0 ? <p className="text-center text-slate-400 p-6 text-sm">Nema razgovora</p>
              : filtered.map(c => (
                <button key={c.phone} onClick={() => openChat(c.phone)}
                  className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 transition ${active?.phone === c.phone ? 'bg-purple-50 border-l-4 border-l-purple-600' : ''}`}
                  data-testid={`chat-conv-${c.phone}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-semibold text-sm text-slate-900 truncate flex-1">{c.client_name || 'Nepoznat'}</div>
                    <div className="text-xs text-slate-400 whitespace-nowrap">{fmtTime(c.last_at)}</div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono mb-1">{c.phone}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 truncate flex-1">
                      {c.last_direction === 'out' && <span className="text-slate-400">Vi: </span>}
                      {c.last_text || '(prazno)'}
                    </span>
                    {c.unread > 0 && <Badge className="bg-purple-600 text-white text-xs h-5 px-2 flex-shrink-0">{c.unread}</Badge>}
                  </div>
                </button>
              ))}
          </div>
        </Card>

        {/* Chat panel */}
        <Card className="col-span-12 md:col-span-8 lg:col-span-9 flex flex-col overflow-hidden">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 p-8">
              <div className="text-center">
                <ChatCircleDots size={48} weight="duotone" className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm">Izaberite razgovor sa lijeve strane</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="p-4 border-b border-slate-200 bg-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center"><User size={20} weight="duotone" className="text-purple-600" /></div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{active.client?.skraceni_naziv || active.client?.naziv_firme || 'Nepoznat klijent'}</div>
                  <div className="text-xs text-slate-500 font-mono">{active.phone}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => openChat(active.phone)} title="Osvjezi"><ArrowsClockwise size={16} /></Button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
                {active.messages.length === 0 ? <p className="text-center text-slate-400 text-sm">Nema poruka</p>
                  : active.messages.map((m, idx) => {
                    const isOut = m.direction === 'out';
                    const isImg = m.media_url && (m.media_type === 'IMAGE' || /\.(jpe?g|png|gif|webp)$/i.test(m.media_url));
                    const isDoc = m.media_url && !isImg;
                    return (
                      <div key={m.id || idx} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] px-3 py-2 rounded-2xl ${isOut ? 'bg-purple-600 text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'}`}>
                          {isImg && (
                            <a href={m.media_url} target="_blank" rel="noreferrer" className="block mb-1">
                              <img src={m.media_url} alt={m.media_name || 'slika'} className="rounded-lg max-w-[280px] max-h-[280px] object-cover" />
                            </a>
                          )}
                          {isDoc && (
                            <a href={m.media_url} target="_blank" rel="noreferrer" className={`flex items-center gap-2 mb-1 p-2 rounded-md ${isOut ? 'bg-purple-700/40 hover:bg-purple-700/60' : 'bg-slate-100 hover:bg-slate-200'} transition`}>
                              <FileText size={20} weight="duotone" className={isOut ? 'text-white' : 'text-purple-600'} />
                              <div className="flex-1 min-w-0">
                                <div className={`text-xs font-semibold truncate ${isOut ? 'text-white' : 'text-slate-800'}`}>{m.media_name || 'Dokument'}</div>
                                <div className={`text-[10px] ${isOut ? 'text-purple-100' : 'text-slate-500'}`}>Preuzmi</div>
                              </div>
                            </a>
                          )}
                          {m.text && <div className="text-sm whitespace-pre-wrap break-words leading-snug">{m.text}</div>}
                          <div className={`text-xs mt-1 flex items-center gap-1 ${isOut ? 'text-purple-100' : 'text-slate-400'}`}>
                            <span>{fmtTime(m.at)}</span>
                            {isOut && m.status === 'failed' && <XCircle size={12} className="text-red-300" />}
                            {isOut && m.status === 'sent' && <CheckCircle size={12} />}
                          </div>
                          {isOut && m.error && <div className="text-xs text-red-200 mt-1 italic">{m.error}</div>}
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Reply box */}
              <div className="p-3 border-t border-slate-200 bg-white">
                <div className="flex gap-2 items-end">
                  <Button onClick={onPickFile} disabled={sending} variant="outline" className="h-10 border-slate-300" title="Pricvrsti fajl ili sliku (max 200MB)" data-testid="chat-attach-btn">
                    <Paperclip size={18} weight="bold" />
                  </Button>
                  <input ref={fileInputRef} type="file" className="hidden" onChange={onFileChange}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.zip,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mp3,.txt" data-testid="chat-attach-file" />
                  <textarea value={reply} onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); sendReply(); } }}
                    placeholder="Otkucajte odgovor... (Ctrl+Enter za slanje, prikvačite fajl klikom na 📎)"
                    rows={2} className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-300"
                    data-testid="chat-reply-input" />
                  <Button onClick={sendReply} disabled={sending || !reply.trim()} className="bg-purple-600 hover:bg-purple-700 text-white h-10" data-testid="chat-reply-send">
                    <PaperPlaneTilt size={16} weight="bold" className="mr-1" />{sending ? '...' : 'Pošalji'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
